import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AppShell } from '../components/layout/AppShell';
import LoginPage from '../pages/auth/LoginPage';
import AuthActionPage from '../pages/auth/AuthActionPage';
import DashboardPage from '../pages/dashboard/DashboardPage';
import MonitoringPage from '../pages/monitoring/MonitoringPage';
import MonitoringHistoryPage from '../pages/monitoring/MonitoringHistoryPage';
import AlertsPage from '../pages/alerts/AlertsPage';
import DriversPage from '../pages/drivers/DriversPage';
import VehiclesPage from '../pages/vehicles/VehiclesPage';
import AdminPage from '../pages/admin/AdminPage';
import OperatorPage from '../pages/operator/OperatorPage';
import ReportsPage from '../pages/reports/ReportsPage';
import TripsPage from '../pages/trips/TripsPage';
import LogbookPage from '../pages/logbook/LogbookPage';
import ExpensesPage from '../pages/expenses/ExpensesPage';
import CalendarPage from '../pages/calendar/CalendarPage';
import { NAV_ROUTES } from '../utils/constants';
import type { UserRole } from '../types/user';

function ProtectedRoute({
  children,
  allowedRoles,
}: {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
}) {
  const { isAuthenticated, user } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to={NAV_ROUTES.LOGIN} replace />;
  }

  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    // Redirect to the first accessible route for the user's role
    const roleHome: Record<UserRole, string> = {
      supervisor: NAV_ROUTES.MONITORING,
      executive: NAV_ROUTES.DASHBOARD,
      admin: NAV_ROUTES.ADMIN,
      operator: NAV_ROUTES.OPERATOR,
    };
    return <Navigate to={roleHome[user.role]} replace />;
  }

  return <>{children}</>;
}

function RoleRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to={NAV_ROUTES.LOGIN} replace />;
  const roleHome: Record<UserRole, string> = {
    supervisor: NAV_ROUTES.MONITORING,
    executive: NAV_ROUTES.DASHBOARD,
    admin: NAV_ROUTES.ADMIN,
    operator: NAV_ROUTES.OPERATOR,
  };
  return <Navigate to={roleHome[user.role]} replace />;
}

export default function AppRouter() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      {/* Public */}
      <Route
        path={NAV_ROUTES.LOGIN}
        element={isAuthenticated ? <RoleRedirect /> : <LoginPage />}
      />

      {/* 🚨 Sin redirección, NI SIQUIERA con sesión iniciada — a diferencia de
          /login, que sí redirige. Alguien con su sesión abierta puede estar
          abriendo la invitación de otra persona (un administrador probando el
          alta, un equipo compartido): mandarlo a su tablero gastaría el
          oobCode sin usarlo, y esos códigos son de un solo uso. */}
      <Route path={NAV_ROUTES.AUTH_ACTION} element={<AuthActionPage />} />

      {/* Protected shell */}
      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<RoleRedirect />} />

        <Route
          path={NAV_ROUTES.DASHBOARD}
          element={
            <ProtectedRoute allowedRoles={['executive', 'admin']}>
              <DashboardPage />
            </ProtectedRoute>
          }
        />

        <Route
          path={NAV_ROUTES.MONITORING}
          element={
            <ProtectedRoute allowedRoles={['supervisor', 'admin']}>
              <MonitoringPage />
            </ProtectedRoute>
          }
        />

        <Route
          path={NAV_ROUTES.ALERTS}
          element={
            <ProtectedRoute allowedRoles={['supervisor', 'admin']}>
              <AlertsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path={NAV_ROUTES.DRIVERS}
          element={
            <ProtectedRoute allowedRoles={['supervisor', 'executive', 'admin']}>
              <DriversPage />
            </ProtectedRoute>
          }
        />

        <Route
          path={NAV_ROUTES.VEHICLES}
          element={
            <ProtectedRoute allowedRoles={['supervisor', 'executive', 'admin']}>
              <VehiclesPage />
            </ProtectedRoute>
          }
        />

        <Route
          path={NAV_ROUTES.ADMIN}
          element={
            // 'supervisor' entra a la misma ruta que 'admin' — AdminPage.tsx
            // le muestra solo la pestaña Usuarios, acotada a Operadores de su
            // sub-unidad (issue #77, PRODUCT.md §4.8).
            <ProtectedRoute allowedRoles={['admin', 'supervisor']}>
              <AdminPage />
            </ProtectedRoute>
          }
        />

        <Route
          path={NAV_ROUTES.OPERATOR}
          element={
            <ProtectedRoute allowedRoles={['operator', 'supervisor', 'admin']}>
              <OperatorPage />
            </ProtectedRoute>
          }
        />

        <Route
          path={NAV_ROUTES.LOGBOOK}
          element={
            // Los cuatro roles leen; el servidor acota QUÉ ve cada uno
            // (§4.8). El Ejecutivo entra y no puede registrar ni corregir:
            // la página le esconde los botones y el backend responde 403.
            <ProtectedRoute allowedRoles={['operator', 'supervisor', 'executive', 'admin']}>
              <LogbookPage />
            </ProtectedRoute>
          }
        />

        <Route
          path={NAV_ROUTES.EXPENSES}
          element={
            // Mismo reparto que las novedades: los cuatro roles leen y el
            // servidor acota QUÉ ve cada uno (§4.8). El Ejecutivo entra y no
            // puede registrar ni corregir: la página le esconde los botones y
            // el backend responde 403.
            <ProtectedRoute allowedRoles={['operator', 'supervisor', 'executive', 'admin']}>
              <ExpensesPage />
            </ProtectedRoute>
          }
        />

        <Route
          path={NAV_ROUTES.CALENDAR}
          element={
            <ProtectedRoute allowedRoles={['operator', 'supervisor', 'executive', 'admin']}>
              <CalendarPage />
            </ProtectedRoute>
          }
        />

        <Route
          path={NAV_ROUTES.TRIPS}
          element={
            // El Operador NO entra aquí: ve sus viajes en "Mi Desempeño", y
            // esta pantalla existe para programar, corregir y cancelar, que no
            // son suyos (PRODUCT.md §4.8). El Ejecutivo mira sin actuar.
            <ProtectedRoute allowedRoles={['supervisor', 'executive', 'admin']}>
              <TripsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path={NAV_ROUTES.REPORTS}
          element={
            <ProtectedRoute allowedRoles={['supervisor', 'executive', 'admin']}>
              <ReportsPage />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* FE-27 — histórico de recorrido en página propia (mobile). Va FUERA de
          `AppShell` a propósito: sin topbar, `bottom-nav` ni FABs encima del
          panel de reproducción, como la app de referencia. Mismos roles que
          Monitoreo. */}
      <Route
        path={NAV_ROUTES.MONITORING_HISTORY}
        element={
          <ProtectedRoute allowedRoles={['supervisor', 'admin']}>
            <MonitoringHistoryPage />
          </ProtectedRoute>
        }
      />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
